<template>
  <div class="es" :class="[`es--${size}`, { 'is-open': isOpen, 'is-disabled': disabled }]">
    <button
      ref="trigger"
      type="button"
      class="es__trigger"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="listId"
      :aria-activedescendant="isOpen && activeIndex > -1 ? `${listId}-${activeIndex}` : null"
      :aria-label="ariaLabel || null"
      :disabled="disabled"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <span class="es__value" :class="{ 'is-placeholder': !selected }">
        {{ selected ? selected.label : placeholder }}
      </span>
      <AppIcon name="chevron-down" :size="size === 'sm' ? 14 : 16" :stroke="2.2" />
    </button>

    <Teleport to="body">
      <transition name="es-pop">
        <ul
          v-if="isOpen"
          :id="listId"
          ref="panel"
          class="es__panel"
          role="listbox"
          :style="panelStyle"
          @mousedown.prevent
        >
          <li
            v-for="(opt, i) in options"
            :id="`${listId}-${i}`"
            :key="`${i}-${opt.value}`"
            class="es__option"
            :class="{
              'is-selected': opt.value === modelValue,
              'is-active': i === activeIndex,
              'is-disabled': opt.disabled,
            }"
            role="option"
            :aria-selected="opt.value === modelValue ? 'true' : 'false'"
            :aria-disabled="opt.disabled ? 'true' : null"
            @mouseenter="activeIndex = i"
            @click="choose(opt)"
          >
            <span>{{ opt.label }}</span>
            <AppIcon v-if="opt.value === modelValue" name="check" :size="15" :stroke="2.4" />
          </li>
          <li v-if="!options.length" class="es__empty">No options</li>
        </ul>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import AppIcon from "./AppIcon.vue";

let uid = 0;

// Dropdown with the ECU orange selection colour (a native <select> popup is drawn by the
// browser / OS, so its blue highlight can not be restyled). Works like a v-model select:
//   <EcuSelect v-model="x" :options="[{ value: 1, label: 'One' }]" @change="..." />
// `change` fires after `update:modelValue`, so a handler can read the already-updated model.
export default {
  name: "EcuSelect",
  components: { AppIcon },
  props: {
    modelValue: { default: null },
    options: { type: Array, default: () => [] }, // [{ value, label, disabled? }]
    placeholder: { type: String, default: "Select" },
    size: { type: String, default: "md" }, // "md" | "sm"
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: "" },
  },
  emits: ["update:modelValue", "change"],
  data() {
    uid += 1;
    return {
      listId: `es-list-${uid}`,
      isOpen: false,
      activeIndex: -1,
      panelStyle: {},
    };
  },
  computed: {
    selected() {
      return this.options.find((o) => o.value === this.modelValue) || null;
    },
  },
  beforeUnmount() {
    this.removeListeners();
  },
  methods: {
    toggle() {
      this.isOpen ? this.close() : this.open();
    },
    open() {
      if (this.disabled || this.isOpen) return;
      const idx = this.options.findIndex((o) => o.value === this.modelValue);
      this.activeIndex = idx > -1 ? idx : this.nextEnabled(-1, 1);
      this.isOpen = true;
      this.place();
      this.$nextTick(() => {
        this.place();
        this.scrollActiveIntoView();
      });
      document.addEventListener("mousedown", this.onOutside, true);
      window.addEventListener("resize", this.close);
      window.addEventListener("scroll", this.onScroll, true);
    },
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.removeListeners();
    },
    removeListeners() {
      document.removeEventListener("mousedown", this.onOutside, true);
      window.removeEventListener("resize", this.close);
      window.removeEventListener("scroll", this.onScroll, true);
    },
    onOutside(event) {
      const t = event.target;
      if (this.$refs.trigger && this.$refs.trigger.contains(t)) return;
      if (this.$refs.panel && this.$refs.panel.contains(t)) return;
      this.close();
    },
    onScroll(event) {
      // scrolling inside the open list must not close it
      if (this.$refs.panel && this.$refs.panel.contains(event.target)) return;
      this.close();
    },
    // fixed-position the list under (or over, when short of room) the trigger
    place() {
      const el = this.$refs.trigger;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const count = Math.max(this.options.length, 1);
      const wanted = Math.min(264, count * 40 + 12);
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < wanted + 12 && r.top > spaceBelow;
      this.panelStyle = {
        left: `${r.left}px`,
        minWidth: `${r.width}px`,
        maxHeight: `${Math.max(120, Math.min(264, (openUp ? r.top : spaceBelow) - 16))}px`,
        ...(openUp
          ? { bottom: `${window.innerHeight - r.top + 6}px` }
          : { top: `${r.bottom + 6}px` }),
      };
    },
    nextEnabled(from, dir) {
      const n = this.options.length;
      for (let step = 1; step <= n; step++) {
        const i = (from + dir * step + n * 2) % n;
        if (!this.options[i].disabled) return i;
      }
      return -1;
    },
    scrollActiveIntoView() {
      const panel = this.$refs.panel;
      const el = panel && panel.querySelector(".is-active");
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    },
    choose(opt) {
      if (opt.disabled) return;
      this.$emit("update:modelValue", opt.value);
      this.$emit("change", opt.value);
      this.close();
      this.$refs.trigger && this.$refs.trigger.focus();
    },
    onTriggerKeydown(event) {
      const key = event.key;
      if (!this.isOpen) {
        if (["ArrowDown", "ArrowUp", "Enter", " "].includes(key)) {
          event.preventDefault();
          this.open();
        }
        return;
      }
      if (key === "Escape") {
        event.preventDefault();
        event.stopPropagation(); // do not also close an enclosing modal
        this.close();
      } else if (key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        this.activeIndex = this.nextEnabled(this.activeIndex, key === "ArrowDown" ? 1 : -1);
        this.$nextTick(this.scrollActiveIntoView);
      } else if (key === "Home" || key === "End") {
        event.preventDefault();
        this.activeIndex =
          key === "Home" ? this.nextEnabled(-1, 1) : this.nextEnabled(0, -1);
        this.$nextTick(this.scrollActiveIntoView);
      } else if (key === "Enter" || key === " ") {
        event.preventDefault();
        if (this.activeIndex > -1) this.choose(this.options[this.activeIndex]);
      } else if (key === "Tab") {
        this.close();
      }
    },
  },
};
</script>

<style scoped>
.es {
  position: relative;
  display: block;
  width: 100%;
}
.es__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #e3e6f0;
  background: #fff;
  color: var(--ecu-ink, #1d2a4d);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.es--md .es__trigger {
  height: 44px;
  padding: 0 14px 0 16px;
  border-radius: 12px;
  font-size: 14px;
}
.es--sm .es__trigger {
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  font-size: var(--ecu-fs-body);
}
.es__trigger:hover:not(:disabled) {
  border-color: #f3b98f;
}
.es__trigger:focus-visible,
.es.is-open .es__trigger {
  outline: none;
  border-color: var(--ecu-orange, #f97316);
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.14);
}
.es__trigger .app-icon {
  color: var(--ecu-navy, #0b1f4b);
  transition: transform 0.2s ease;
}
.es.is-open .es__trigger .app-icon {
  transform: rotate(180deg);
  color: var(--ecu-orange, #f97316);
}
.es__value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.es__value.is-placeholder {
  color: #8b90a3;
}
.es.is-disabled .es__trigger {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ---- list (teleported to <body>) ---- */
.es__panel {
  position: fixed;
  z-index: 3000;
  margin: 0;
  padding: 6px;
  list-style: none;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #eef0f6;
  border-radius: 14px;
  box-shadow: 0 18px 44px rgba(20, 40, 90, 0.2);
  font-family: "Plus Jakarta Sans", "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
}
.es__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: var(--ecu-fs-body);
  color: #1d2a4d;
  cursor: pointer;
  user-select: none;
  transition: background 0.12s ease, color 0.12s ease;
}
.es__option.is-active {
  background: #fff1e6;
  color: #e84d00;
}
.es__option.is-selected {
  background: #ffe3cc;
  color: #d94600;
  font-weight: 700;
}
.es__option.is-selected.is-active {
  background: #ffd9bd;
}
.es__option.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.es__empty {
  padding: 10px 12px;
  font-size: var(--ecu-fs-body);
  color: #8b90a3;
}
.es-pop-enter-active,
.es-pop-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.es-pop-enter-from,
.es-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
@media (prefers-reduced-motion: reduce) {
  .es__trigger,
  .es__trigger .app-icon,
  .es-pop-enter-active,
  .es-pop-leave-active {
    transition: none;
  }
}
</style>